import { At, CaretLeft, Plus, User, X } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { ErrorMessage, Form, Formik } from "formik";
import * as yup from "yup";
import useUsernameEnquiry from "../../../../hooks/useUsernameEnquiry";
import { UsernameEnquiryData } from "../../../../@types";
import { useSendContext } from "../../../../context/SendContext";
import { HashLoader } from "react-spinners";
import useBeneficiaries from "../../../../hooks/useBeneficiaries";
import useAddBeneficiary from "../../../../hooks/useAddBeneficiary";
import { toast } from "sonner";
import { motion } from "framer-motion";

const SCHEMA = yup.object().shape({
  username: yup
    .string()
    .required("Enter the username of the user you want to send money to"),
});

const SearchUser = () => {
  const navigate = useNavigate();
  const { setTransfer, transfer } = useSendContext();

  const [username, setUsername] = useState<string | null>(
    transfer?.username || null
  );

  const [foundUser, setFoundUser] = useState<UsernameEnquiryData | null>(null);

  const { checkUsername, isEnquiryError, enquiryError, fetchingUsername } =
    useUsernameEnquiry({
      onSuccess(data, variables) {
        if (data.data) {
          setUsername(variables.username);
          setFoundUser(data.data);
        }
      },
    });
  const { beneficiaries } = useBeneficiaries();
  const [beneficiaryListOpen, setBeneficiaryListOpen] = useState(false);

  const {
    addBeneficiary,
    addingBeneficiary,
    addingBeneficiaryError,
    isAddBeneficiaryError,
  } = useAddBeneficiary({
    onSuccess() {
      toast.success("Beneficiary added successfully");
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const handleAddToBeneficiary = useCallback(() => {
    if (!foundUser || !username) return;

    const existingBeneficiary = beneficiaries?.data?.find(
      (beneficiary) => beneficiary.visaro_username === username
    );

    if (existingBeneficiary) {
      toast.error("This user is already in your beneficiary list.");
      return;
    }

    addBeneficiary({
      visaro_username: username,
      visaro_user: true,
      account_name: foundUser.name,
    });
  }, [foundUser, beneficiaries, addBeneficiary, username]);

  const handleContinue = useCallback(
    (values: { username: string }) => {
      if (foundUser && username) {
        setTransfer({
          username: username,
          transferType: "intra",
          recipientName: foundUser.name,
        });
        navigate("amount");
      } else {
        setUsername(values.username);
        checkUsername({ username: values.username });
      }
    },
    [username, foundUser, setTransfer, navigate, checkUsername]
  );

  return (
    <div>
      {fetchingUsername && (
        <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        validationSchema={SCHEMA}
        initialValues={{
          username: transfer?.username || "",
        }}
        onSubmit={handleContinue}
      >
        {({ isValid, handleChange, handleBlur, setFieldValue, values }) => {
          return (
            <div className="flex items-center justify-center gap-5 px-5 py-20 text-center flex-col">
              <div className="flex flex-col lg:gap-2">
                <div className="font-semibold text-2xl lg:text-3xl">
                  Send money to a Visaro user
                </div>
                <div className="text-gray-500">
                  Enter the username of the user you want to send money to
                </div>
              </div>

              <div className="flex gap-5 w-full max-w-4xl flex-col lg:flex-row">
                <Form className="col-span-2 lg:bg-white lg:drop-shadow-sm lg:p-5 rounded-xl flex flex-col gap-5 w-full">
                  <div className="flex gap-5">
                    <div className="border border-gray-300 rounded-lg flex items-center w-full relative">
                      <At className="absolute left-0 mx-4" />
                      <input
                        className="flex-1 p-4 py-3 focus:outline-none bg-transparent pl-12"
                        type="text"
                        name="username"
                        value={values.username}
                        placeholder="Username"
                        onBlur={handleBlur("username")}
                        onInput={() => {
                          setUsername(null);
                          setFoundUser(null);
                        }}
                        onChange={handleChange("username")}
                      />
                    </div>
                  </div>
                  <ErrorMessage
                    name="username"
                    component="div"
                    className="text-red-500 text-left"
                  />
                  {foundUser && (
                    <div className="bg-gray-50 p-4 rounded-lg flex gap-4 items-center text-left">
                      <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                        <User weight="bold" size={18} />
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-medium">
                          {foundUser.name}
                        </div>
                        <div className="text-sm text-gray-700">Visaro User</div>
                      </div>
                    </div>
                  )}
                  {isEnquiryError && (
                    <div className="w-full text-left text-red-500">
                      {enquiryError?.message}
                    </div>
                  )}
                  {isAddBeneficiaryError && (
                    <div className="w-full text-left text-red-500">
                      {addingBeneficiaryError?.message}
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    {foundUser ? (
                      <button
                        type="button"
                        onClick={handleAddToBeneficiary}
                        className="text-visaro-primary-700 flex items-center gap-2 p-2 hover:bg-gray-50 px-4 rounded-md transition"
                      >
                        <Plus />
                        <span>
                          {addingBeneficiary
                            ? "Adding..."
                            : "Add to beneficiary list"}
                        </span>
                      </button>
                    ) : (
                      <div></div>
                    )}
                    {beneficiaries?.data &&
                      (beneficiaries?.data?.length ? (
                        !foundUser && (
                          <button
                            onClick={() =>
                              setBeneficiaryListOpen((prev) => !prev)
                            }
                            type="button"
                            className="text-visaro-primary-700 flex items-center gap-2 p-2 hover:bg-gray-50 px-4 rounded-md transition"
                          >
                            <span>
                              {beneficiaryListOpen ? "Hide" : "Show"}{" "}
                              beneficiary list
                            </span>
                          </button>
                        )
                      ) : (
                        <></>
                      ))}
                  </div>
                  <div className="flex justify-between w-full max-w-4xl gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                    >
                      <CaretLeft weight="bold" size={20} />
                      <span>Back</span>
                    </button>
                    <button
                      disabled={!isValid || fetchingUsername}
                      type="submit"
                      className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 border-deepBlue rounded-xl font-medium"
                    >
                      {foundUser
                        ? "Continue"
                        : fetchingUsername
                        ? "Checking....."
                        : "Confirm"}
                    </button>
                  </div>
                </Form>
                {beneficiaryListOpen && (
                  <motion.div
                    initial={{ opacity: 0, width: "50%" }}
                    animate={{ opacity: 1, width: "100%" }}
                    exit={{ opacity: 0, width: "50%" }}
                    className="p-4 rounded-xl bg-white drop-shadow-sm max-w-3xl w-full text-left flex flex-col gap-4 items-start"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <div className="font-medium">Your Beneficiaries</div>
                        <div className="text-sm text-gray-500">
                          Select a beneficiary to send money to
                        </div>
                      </div>
                      <X
                        className="p-2 bg-gray-50 rounded-full size-8 cursor-pointer"
                        onClick={() => {
                          setBeneficiaryListOpen(false);
                        }}
                      />
                    </div>
                    <div className="w-full">
                      {beneficiaries?.data?.length ? (
                        beneficiaries.data
                          .filter((beneficiary) => beneficiary.visaro_user)
                          .map((beneficiary) => (
                            <div
                              key={beneficiary.visaro_username}
                              className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg transition cursor-pointer"
                              onClick={() => {
                                if (beneficiary.visaro_username) {
                                  setFieldValue(
                                    "username",
                                    beneficiary.visaro_username
                                  );
                                  setUsername(beneficiary.visaro_username);
                                  setFoundUser({
                                    name: beneficiary.name,
                                  });
                                  setBeneficiaryListOpen(false);
                                }
                              }}
                            >
                              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                                <User weight="bold" size={18} />
                              </div>
                              <div className="flex-1 flex flex-col items-start">
                                <div className="font-medium">
                                  {beneficiary.name || "Unknown User"}
                                </div>
                                <div className="text-sm text-gray-700">
                                  Visaro User
                                </div>
                              </div>
                            </div>
                          ))
                      ) : (
                        <div className="text-gray-500">
                          No beneficiaries found
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default SearchUser;
